/**
 * SakerUtenRettshjelp - Oversikt over saker uten registrert rettshjelpsdekning
 * Muliggjør å sjekke og bekrefte om det foreligger dekning eller ikke
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './SakerUtenRettshjelp.css';

export default function SakerUtenRettshjelp() {
  const [saker, setSaker] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle'); // 'alle', 'usjekkede', 'ingen_dekning'
  const [visRedigerModal, setVisRedigerModal] = useState(false);
  const [valgtSak, setValgtSak] = useState(null);

  useEffect(() => {
    lastSakerUtenRettshjelp();
  }, []);

  const lastSakerUtenRettshjelp = async () => {
    try {
      setLoading(true);
      const data = await crmApi.getSaker();
      const alleSaker = Array.isArray(data) ? data : (data.saker || []);

      // Filtrer ut saker UTEN rettshjelp
      const utenRettshjelp = alleSaker.filter(s =>
        !s.rettshjelp_bkf_selskap_id &&
        !s.rettshjelp_innbo_selskap_id &&
        !s.rettshjelp_bkf_selskap_navn &&
        !s.rettshjelp_innbo_selskap_navn
      );

      setSaker(utenRettshjelp);
    } catch (error) {
      console.error('Feil ved lasting av saker uten rettshjelp:', error);
      alert('Kunne ikke laste saker');
    } finally {
      setLoading(false);
    }
  };

  const filtrerteSaker = saker.filter(s => {
    if (filter === 'usjekkede') {
      return !s.rettshjelp_status || s.rettshjelp_status === 'ikke_sjekket';
    }
    if (filter === 'ingen_dekning') {
      return s.rettshjelp_status === 'ingen_dekning';
    }
    return true; // 'alle'
  });

  const apneRedigerModal = (sak) => {
    setValgtSak(sak);
    setVisRedigerModal(true);
  };

  const lukkModal = () => {
    setVisRedigerModal(false);
    setValgtSak(null);
  };

  const oppdaterRettshjelpStatus = async (status, notat = '') => {
    if (!valgtSak) return;

    try {
      await crmApi.updateSak(valgtSak.id, {
        rettshjelp_status: status,
        rettshjelp_sjekket_dato: new Date().toISOString().split('T')[0],
        rettshjelp_notat: notat
      });

      // Oppdater lokal state
      setSaker(prev => prev.map(s =>
        s.id === valgtSak.id
          ? { ...s, rettshjelp_status: status, rettshjelp_sjekket_dato: new Date().toISOString().split('T')[0], rettshjelp_notat: notat }
          : s
      ));

      lukkModal();
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
      alert('Kunne ikke oppdatere status');
    }
  };

  if (loading) {
    return (
      <div className="saker-uten-rettshjelp loading">
        <div className="spinner"></div>
        <p>Laster saker uten rettshjelp...</p>
      </div>
    );
  }

  return (
    <div className="saker-uten-rettshjelp">
      <div className="header">
        <h1>🔍 Saker uten rettshjelpsdekning</h1>
        <p className="subtitle">{saker.length} saker mangler registrert rettshjelp</p>
      </div>

      <div className="filter-buttons">
        <button
          className={`filter-btn ${filter === 'alle' ? 'active' : ''}`}
          onClick={() => setFilter('alle')}
        >
          Alle ({saker.length})
        </button>
        <button
          className={`filter-btn ${filter === 'usjekkede' ? 'active' : ''}`}
          onClick={() => setFilter('usjekkede')}
        >
          Ikke sjekket ({saker.filter(s => !s.rettshjelp_status || s.rettshjelp_status === 'ikke_sjekket').length})
        </button>
        <button
          className={`filter-btn ${filter === 'ingen_dekning' ? 'active' : ''}`}
          onClick={() => setFilter('ingen_dekning')}
        >
          Bekreftet ingen dekning ({saker.filter(s => s.rettshjelp_status === 'ingen_dekning').length})
        </button>
      </div>

      {filtrerteSaker.length === 0 ? (
        <div className="empty-state">
          <p>Ingen saker i denne kategorien</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="saker-table">
            <thead>
              <tr>
                <th>Saksnr</th>
                <th>Klient</th>
                <th>Status</th>
                <th>Sjekket dato</th>
                <th>Notat</th>
                <th>Handling</th>
              </tr>
            </thead>
            <tbody>
              {filtrerteSaker.map(sak => (
                <tr key={sak.id}>
                  <td><strong>{sak.saksnummer}</strong></td>
                  <td>{sak.klient_navn || '-'}</td>
                  <td>
                    {!sak.rettshjelp_status || sak.rettshjelp_status === 'ikke_sjekket' ? (
                      <span className="badge badge-warning">⚠️ Ikke sjekket</span>
                    ) : sak.rettshjelp_status === 'ingen_dekning' ? (
                      <span className="badge badge-danger">❌ Ingen dekning</span>
                    ) : (
                      <span className="badge badge-info">{sak.rettshjelp_status}</span>
                    )}
                  </td>
                  <td>{sak.rettshjelp_sjekket_dato || '-'}</td>
                  <td className="notat-cell">{sak.rettshjelp_notat || '-'}</td>
                  <td>
                    <button
                      className="btn-small btn-primary"
                      onClick={() => apneRedigerModal(sak)}
                    >
                      Sjekk dekning
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for å registrere rettshjelp-status */}
      {visRedigerModal && valgtSak && (
        <div className="modal-overlay" onClick={lukkModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sjekk rettshjelpsdekning</h2>
              <button className="close-btn" onClick={lukkModal}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="sak-info">
                <p><strong>Sak:</strong> {valgtSak.saksnummer}</p>
                <p><strong>Klient:</strong> {valgtSak.klient_navn}</p>
              </div>

              <div className="status-valg">
                <h3>Hva er status for rettshjelpsdekning?</h3>

                <button
                  className="status-btn status-ingen-dekning"
                  onClick={() => {
                    const notat = prompt('Bekreft at det ikke foreligger rettshjelpsdekning. Legg gjerne til et notat (valgfritt):');
                    if (notat !== null) { // null = bruker kansellerte
                      oppdaterRettshjelpStatus('ingen_dekning', notat);
                    }
                  }}
                >
                  ❌ Bekreftet ingen dekning
                </button>

                <button
                  className="status-btn status-sjekkes-senere"
                  onClick={() => {
                    const notat = prompt('Hvorfor må dette sjekkes senere? (valgfritt notat):');
                    if (notat !== null) {
                      oppdaterRettshjelpStatus('sjekkes_senere', notat);
                    }
                  }}
                >
                  ⏳ Må sjekkes senere
                </button>

                <button
                  className="status-btn status-venter-svar"
                  onClick={() => {
                    const notat = prompt('Hvem venter vi svar fra? (valgfritt notat):');
                    if (notat !== null) {
                      oppdaterRettshjelpStatus('venter_svar', notat);
                    }
                  }}
                >
                  📧 Venter svar fra klient
                </button>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={lukkModal}>Avbryt</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="summary">
        <h3>📊 Oppsummering</h3>
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-number">{saker.filter(s => !s.rettshjelp_status || s.rettshjelp_status === 'ikke_sjekket').length}</div>
            <div className="summary-label">Ikke sjekket</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{saker.filter(s => s.rettshjelp_status === 'ingen_dekning').length}</div>
            <div className="summary-label">Bekreftet ingen dekning</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{saker.filter(s => s.rettshjelp_status === 'venter_svar').length}</div>
            <div className="summary-label">Venter svar</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{saker.length}</div>
            <div className="summary-label">Totalt</div>
          </div>
        </div>
      </div>
    </div>
  );
}
